import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { fromEvent, of } from 'rxjs';
import { catchError, debounceTime, filter, map, switchMap } from 'rxjs/operators';

import { JobStatusUtils } from '@core/models/optimization.model';
import { PlanCheckStatus } from '@core/models/plan-finding.model';
import { FindingsVisibilityService } from '@core/services/plan-findings/findings-visibility.service';
import { PlansService } from '@core/services/plans/plans.service';
import * as PlanActions from '@core/store/plan/plan.actions';
import { isNil } from '@core/utils/shared-utils';

import { PlanFindingsActions } from './plan-findings.actions';
import {
  selectFindingsCheckContext,
  selectFindingsEditSeq,
  selectPlanFindingsState
} from './plan-findings.selectors';
import { PlanFindingsState } from './plan-findings.state';

/**
 * How long the plan must go unedited before its findings are refreshed on their own.
 * Long enough that a run of edits counts as one editing session.
 */
export const FINDINGS_QUIET_PERIOD_MS = 60_000;

function errorMessageOf(error: unknown): string {
  const message = (error as { errorMessage?: string } | null)?.errorMessage;
  return message ?? String(error);
}

function hasContext(
  state: PlanFindingsState
): state is PlanFindingsState & { planId: string; paramId: number } {
  return !isNil(state.planId) && !isNil(state.paramId);
}

/** A plan never checked and a check older than the plan's data say nothing about the plan now. */
function isUnusable(state: PlanFindingsState): boolean {
  return state.status === PlanCheckStatus.NOT_CHECKED || state.isStale;
}

@Injectable()
export class PlanFindingsEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly plansService = inject(PlansService);
  private readonly document = inject(DOCUMENT);
  private readonly visibility = inject(FindingsVisibilityService);

  readOnContextChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PlanFindingsActions.contextChanged),
      map(({ planId, paramId }) => PlanFindingsActions.readRequested({ planId, paramId }))
    )
  );

  /**
   * The plan can be edited where this tab cannot see it — another tab, another user,
   * the API — and nothing announces that. Revealing the tab is the moment a human is
   * about to read the findings, so it is the moment to make sure they are the server's
   * and not a snapshot from whenever this tab was last looked at.
   */
  readOnTabReveal$ = createEffect(() =>
    fromEvent(this.document, 'visibilitychange').pipe(
      filter(() => this.document.visibilityState === 'visible'),
      concatLatestFrom(() => this.store.select(selectPlanFindingsState)),
      filter(([, state]) => hasContext(state)),
      map(([, state]) =>
        PlanFindingsActions.readRequested({
          planId: state.planId as string,
          paramId: state.paramId as number
        })
      )
    )
  );

  read$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PlanFindingsActions.readRequested),
      concatLatestFrom(() => this.store.select(selectPlanFindingsState)),
      // A check overwrites the very row a read fetches, and which of the two the server
      // orders first cannot be known here: the read's payload may or may not already
      // include the check's write. So it is never the fresher answer and is not worth
      // issuing — the check's own result is what the panel should show. Suppressed here
      // rather than per trigger, so no future caller of `readRequested` has to know.
      filter(([, state]) => state.phase !== 'CHECKING'),
      switchMap(([{ planId, paramId }]) =>
        this.plansService.getPlanFindings$({ planId, paramId }).pipe(
          map((response) =>
            PlanFindingsActions.readSucceeded({ response, planId, paramId })
          ),
          catchError((error) =>
            of(
              PlanFindingsActions.requestFailed({
                error: errorMessageOf(error),
                planId,
                paramId
              })
            )
          )
        )
      )
    )
  );

  /**
   * A plan that has never been checked and a plan whose check predates its data are
   * both unusable as an answer to "what is wrong with my plan", so both earn one, once
   * the findings are on screen to show it running.
   */
  checkWhenUnusable$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PlanFindingsActions.readSucceeded),
      filter(() => this.visibility.visible),
      concatLatestFrom(() => this.store.select(selectPlanFindingsState)),
      filter(
        ([{ response }, state]) =>
          hasContext(state) &&
          (response.status === PlanCheckStatus.NOT_CHECKED || response.isStale)
      ),
      map(([, state]) =>
        PlanFindingsActions.checkRequested({
          planId: state.planId as string,
          paramId: state.paramId as number
        })
      )
    )
  );

  /** The check an unusable read skipped while the findings were off screen. */
  checkWhenShown$ = createEffect(() =>
    this.visibility.visible$.pipe(
      filter((visible) => visible),
      concatLatestFrom(() => this.store.select(selectPlanFindingsState)),
      filter(
        ([, state]) => hasContext(state) && state.phase === 'READY' && isUnusable(state)
      ),
      map(([, state]) =>
        PlanFindingsActions.checkRequested({
          planId: state.planId as string,
          paramId: state.paramId as number
        })
      )
    )
  );

  autoRecheck$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PlanFindingsActions.planDataChanged),
      debounceTime(FINDINGS_QUIET_PERIOD_MS),
      filter(() => this.visibility.visible),
      concatLatestFrom(() => this.store.select(selectPlanFindingsState)),
      filter(([, state]) => hasContext(state) && state.isStale),
      map(([, state]) =>
        PlanFindingsActions.checkRequested({
          planId: state.planId as string,
          paramId: state.paramId as number
        })
      )
    )
  );

  readOnBlockedRun$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PlanActions.optimizationUpdated),
      filter(({ result }) => JobStatusUtils.isBlockedStatus(result.status)),
      concatLatestFrom(() => this.store.select(selectPlanFindingsState)),
      filter(([{ result }, state]) => hasContext(state) && state.planId === result.planId),
      map(([, state]) =>
        PlanFindingsActions.readRequested({
          planId: state.planId as string,
          paramId: state.paramId as number
        })
      )
    )
  );

  /** Asked from wherever a Check button is, so no caller has to resolve the plan's context. */
  checkOnAsk$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PlanFindingsActions.checkAsked),
      concatLatestFrom(() => this.store.select(selectFindingsCheckContext)),
      filter(([, context]) => !isNil(context)),
      map(([, context]) =>
        PlanFindingsActions.checkRequested(context as { planId: string; paramId: number })
      )
    )
  );

  check$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PlanFindingsActions.checkRequested),
      concatLatestFrom(() => this.store.select(selectFindingsEditSeq)),
      switchMap(([{ planId, paramId }, editSeq]) =>
        this.plansService.checkPlan$({ planId, paramId }).pipe(
          map((response) =>
            PlanFindingsActions.checkSucceeded({ response, planId, paramId, editSeq })
          ),
          catchError((error) =>
            of(
              PlanFindingsActions.requestFailed({
                error: errorMessageOf(error),
                planId,
                paramId
              })
            )
          )
        )
      )
    )
  );
}
